import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIncidentStore } from '@/store/incident.store';
import { useFieldStore } from '@/store/field.store';
import { api } from '@/services/api';
import { toast } from '@/utils/toast';
import type { RoadClosure, PaginatedRoadClosures } from '@/types/infrastructure';
import FaIcon from '@/components/common/FaIcon';
import EmptyState from '@/components/common/EmptyState';
import ErrorState from '@/components/common/ErrorState';
import { PlusIcon } from '@/components/icons';
import LoadingState from '@/components/common/LoadingState';
import { parseApiError, getErrorMessage } from '@/utils/api-errors';
import { IconButton } from '@/components/common/Button';
import MetricValue from '@/components/common/MetricValue';
import { SEVERITY_SPINE, type Severity } from '@/components/common/severity';

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  planned: { bg: 'bg-primary-100 dark:bg-primary-900/30', text: 'text-primary-800 dark:text-primary-300' },
  active: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300' },
  cleared: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300' },
};

// Active road closures dominate; planned are elevated; cleared are calm.
const CLOSURE_RANK: Record<string, Severity> = {
  active: 'critical',
  planned: 'elevated',
  cleared: 'calm',
};

export default function FieldRoadClosures() {
  const navigate = useNavigate();
  const { activeIncident } = useIncidentStore();
  const { setDrawingMode } = useFieldStore();

  const handleNewClosure = useCallback(() => {
    setDrawingMode('road_closure');
  }, [setDrawingMode]);
  const [closures, setClosures] = useState<RoadClosure[]>([]);
  // The server's UNPAGINATED total, kept separate from `closures.length`. The
  // header used to print `closures.length` labelled "total", which is only the
  // size of the page the API happened to return — during a countywide flood with
  // 60 closures the responder saw "20 total" and had no way to know 40 more
  // roads were closed. Never label a page size as a total.
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // A failed fetch must be distinguishable from a genuinely empty list. Without
  // this, a timeout / 503 / lost signal fired a transient toast and left
  // `closures` at [], so once the toast dismissed the page affirmatively told a
  // responder "No road closures" — and they could route a vehicle onto a road
  // that is in fact flooded and closed. Never let an unknown render as "none".
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadClosures = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const params = new URLSearchParams();
      if (activeIncident?.id) params.append('incident_id', activeIncident.id);
      // Without an explicit limit the backend defaults to 20 rows
      // (infrastructure.service.ts listRoadClosures). 100 is the real ceiling —
      // the service clamps with Math.min(limit, DEFAULT_MAX_PAGE_SIZE=100), so
      // asking for more is silently reduced and the Zod max of 500 is
      // unreachable. Anything the server withholds beyond this is surfaced by
      // the "Showing X of Y" banner below, never silently dropped.
      params.append('limit', '100');
      params.append('page', '1');
      const result = await api.get<PaginatedRoadClosures>(`/infrastructure/road-closures?${params}`);
      const rows = result.data || [];
      setClosures(rows);
      setTotal(typeof result.total === 'number' ? result.total : rows.length);
    } catch (err) {
      setLoadError(getErrorMessage(parseApiError(err)));
      toast.error('Failed to load road closures');
    } finally {
      setIsLoading(false);
    }
  }, [activeIncident?.id]);

  useEffect(() => {
    loadClosures();
  }, [loadClosures]);

  if (isLoading) {
    return <LoadingState message="Loading closures..." />;
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="heading-3">Road Closures</h2>
        <div className="flex items-center gap-2">
          {/* Suppressed while errored: "0 total" reads as a confirmed count of
              open roads and reinforces the same false negative. */}
          {!loadError && (
            <span className="text-sm text-theme-tertiary"><MetricValue value={total} className="font-semibold" /> total</span>
          )}
          <IconButton icon={<FaIcon icon="arrows-rotate" className="h-4 w-4" />} label="Refresh road closures" onClick={() => loadClosures()} variant="ghost" size="sm" />
        </div>
      </div>

      {/* A truncated list must announce itself. The list is capped at the API's
          100-row ceiling; if the tenant/incident has more closures than that,
          silently omitting the remainder lets a responder plan a route through a
          closure that exists in the system but is not on this screen. Suppressed
          while errored for the same reason the count is: an unknown must never
          render as a confirmed number. */}
      {!loadError && total > closures.length && (
        <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200">
          Showing {closures.length} of {total} closures — open the map for all closures.
        </div>
      )}

      {loadError ? (
        <ErrorState
          title="Failed to load road closures"
          message={`${loadError} Closure data may be missing — do not treat this screen as confirmation that roads are open.`}
          onRetry={() => loadClosures()}
        />
      ) : closures.length === 0 ? (
        <EmptyState
          variant="operational"
          icon="road-barrier"
          title="No road closures"
          description="Report a road closure to get started."
          actionLabel="New Road Closure"
          onAction={handleNewClosure}
        />
      ) : (
        <div className="space-y-2">
          {closures.map((c) => {
            const color = STATUS_COLORS[c.status] || STATUS_COLORS.active;
            const rank: Severity = CLOSURE_RANK[c.status] ?? 'calm';
            return (
              <button
                key={c.id}
                onClick={() => navigate(`/field/roads/${c.id}`)}
                className={`w-full text-left bg-surface-primary rounded-lg border border-theme-primary p-3 min-h-[44px] touch-manipulation active:bg-surface-secondary ${SEVERITY_SPINE[rank]}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-theme-primary truncate">{c.name}</p>
                    <p className="text-xs text-theme-tertiary mt-0.5">
                      {c.closure_type || 'full'} &middot; {c.reason || 'No reason'}
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color.bg} ${color.text}`}>
                    {c.status}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* FAB */}
      <button
        onClick={handleNewClosure}
        aria-label="Report road closure"
        className="fixed right-4 z-30 w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center touch-manipulation active:bg-primary-700"
        style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom) + 1rem)' }}
      >
        <PlusIcon className="h-7 w-7" />
      </button>
    </div>
  );
}
